import React, { useEffect, useState } from 'react'
import HFTextInput from 'components/HFInputs/HFTextInput'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'

import styles from '../Dialog.module.sass'
import { useHistory, useLocation } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { RefuseTaskSchema } from './RefuseTaskSchema'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'
import useSubmitTask from 'hooks/useSubmitTask'
import { API_URL, parseJSONContent } from 'store/fetchUtils'
import { RequisitionType, RequisitionStatus } from 'utils/enums'

const RefuseTaskController = (props) => {
	const { i18n } = useGlobal()
	const { data, resolve, dismiss, id } = props
	const history = useHistory()
	const location = useLocation()
	const appointmentIds = location.state?.appointmentId
	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(RefuseTaskSchema),
		shouldUnregister: true,
	})
	const { handleSubmit, formState } = methods
	const [customError, setCustomError] = useState([])
	const { error, submitting, refuseTask } = useSubmitTask(
		data?.id,
		formState?.errors
	)

	const [loadingConnectionOptions, setLoadingConnectionOptions] =
		useState(false)
	const [hasFetchedConnection, setHasFetchedConnection] = useState(false)
	const [connectionOptions, setConnectionOptions] = useState([])
	const [warningAppointmentId, setWarningAppointmentId] = useState(null)
	const [selectedConnectionOption, setSelectedConnectionOption] =
		useState(null)
	const [decidirMaisTarde, setDecidirMaisTarde] = useState(false)
	const [connectionError, setConnectionError] = useState(false)

	const showConnectionSection =
		loadingConnectionOptions ||
		(hasFetchedConnection &&
			(connectionOptions.length > 0 || warningAppointmentId !== null))

	const formatDate = (iso) => {
		if (!iso) return ''
		return new Date(iso).toLocaleDateString('pt-PT')
	}

	useEffect(() => {
		if (data?.requisitionType?.id !== RequisitionType.Appointment) return
		if (data?.requisitionState?.id === RequisitionStatus.WaitingApproval)
			return

		const appointmentId = appointmentIds ?? data?.appointmentId
		const processId = data?.processId
		if (!appointmentId || !processId) return

		const token =
			localStorage.getItem('session-token') ||
			sessionStorage.getItem('session-token')
		if (!token) return

		setLoadingConnectionOptions(true)
		const requisitionId = data?.requisitionId
		const qs = requisitionId ? `?requisitionId=${requisitionId}` : ''
		fetch(
			`${API_URL}/api/clinical_processes/${processId}/appointments/${appointmentId}/cancellation-link${qs}`,
			{ method: 'GET', headers: { Authorization: `Bearer ${token}` } }
		)
			.then((r) => parseJSONContent(r))
			.then((res) => {
				if (!res?.mensagem) {
					const opts = res?.connectionOptions ?? []
					setConnectionOptions(opts)
					if (res?.warningAppointmentId) {
						setWarningAppointmentId(res.warningAppointmentId)
					}
					if (opts.length === 0) {
						setDecidirMaisTarde(true)
					}
				} else {
					setDecidirMaisTarde(true)
				}
			})
			.catch(() => {
				setDecidirMaisTarde(true)
			})
			.finally(() => {
				setLoadingConnectionOptions(false)
				setHasFetchedConnection(true)
			})
	}, [])

	const onSubmit = (formData) => {
		if (
			data?.requisitionType?.id === 1 &&
			hasFetchedConnection &&
			!decidirMaisTarde &&
			selectedConnectionOption === null
		) {
			setConnectionError(true)
			return
		}

		const idConsultaLigacao = decidirMaisTarde
			? null
			: selectedConnectionOption?.appointmentId
			? Number(selectedConnectionOption.appointmentId)
			: null

		refuseTask({
			...formData,
			type: data.type,
			transferAppointments: [],
			idConsultaLigacao,
			appointmentId: appointmentIds ?? data?.appointmentId ?? null,
			decidirMaisTarde,
		})
			.then(() => {
				resolve()
			})
			.catch(() => {})
	}

	return (
		<>
			<TaskHeaderErrors
				errors={[
					...(error ?? []),
					...customError.map((e) => `${e.title}: ${e.text}`),
				]}
			/>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
					submissionType='refuse'
				/>
			)}
			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<form
						id={`form_refuse_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						<h4
							className={`${styles['form-title']}`}
						>{i18n`Refuse requisition`}</h4>
						<fieldset>
							<h5
								className={styles['input-title']}
							>{i18n`Refusal reason`}</h5>
							<HFTextInput
								name='justification'
								placeholder={i18n`Justification`}
								multiline={true}
								rows={5}
								noResize
							/>
						</fieldset>

						{showConnectionSection && (
							<fieldset>
								<h5 className={styles['input-title']}>
									{i18n`Decision link appointment`}
								</h5>

								{loadingConnectionOptions && (
									<p className={styles['connection-info']}>
										{i18n`Loading`}...
									</p>
								)}

								{!loadingConnectionOptions && (
									<>
										{warningAppointmentId &&
											connectionOptions.length > 0 && (
												<p
													className={
														styles[
															'warning-message'
														]
													}
												>
													{i18n`A consulta`}{' '}
													{warningAppointmentId}{' '}
													{i18n`vai ficar sem ligação a uma consulta destino. A qual pretende ligar?`}
												</p>
											)}

										<div
											className={
												styles['cancellation-options']
											}
										>
											<div
												className={
													styles['options-list']
												}
											>
												{connectionOptions.length ===
													0 && (
													<p
														className={
															styles[
																'connection-info'
															]
														}
													>
														{i18n`Não há consultas disponíveis para fazer a ligação`}
													</p>
												)}

												{connectionOptions.map(
													(opt) => (
														<div
															key={
																opt.appointmentId
															}
															className={
																styles[
																	'option-item'
																]
															}
														>
															<label>
																<input
																	type='radio'
																	name='connectionOption'
																	value={
																		opt.appointmentId
																	}
																	checked={
																		selectedConnectionOption?.appointmentId ===
																		opt.appointmentId
																	}
																	onChange={() => {
																		setSelectedConnectionOption(
																			opt
																		)
																		setDecidirMaisTarde(
																			false
																		)
																		setConnectionError(
																			false
																		)
																	}}
																/>
																<div
																	className={
																		styles[
																			'option-details'
																		]
																	}
																>
																	<strong>
																		#
																		{
																			opt.appointmentId
																		}
																	</strong>{' '}
																	–{' '}
																	{formatDate(
																		opt.date
																	)}
																</div>
															</label>
														</div>
													)
												)}

												<div
													className={
														styles['option-item']
													}
												>
													<label>
														<input
															type='radio'
															name='connectionOption'
															value='later'
															checked={
																decidirMaisTarde
															}
															onChange={() => {
																setDecidirMaisTarde(
																	true
																)
																setSelectedConnectionOption(
																	null
																)
																setConnectionError(
																	false
																)
															}}
														/>
														<div
															className={
																styles[
																	'option-details'
																]
															}
														>
															{i18n`Decide later`}
														</div>
													</label>
												</div>
											</div>

											{connectionError && (
												<div
													className={
														styles[
															'selection-error'
														]
													}
												>
													⚠{' '}
													{i18n`Por favor selecione uma opção de ligação`}
												</div>
											)}
										</div>
									</>
								)}
							</fieldset>
						)}
					</form>
				</div>
			</FormProvider>
			<menu className={styles['menu']}>
				<ActionButton
					key='cancel'
					secondary={true}
					className={styles['menu-button-secondary']}
					onClick={() => {
						history.goBack()
					}}
				>
					{i18n`Cancel`}
				</ActionButton>
				<ActionButton
					className={styles['menu-button-primary-refuse']}
					form={`form_refuse_${id}`}
				>
					{i18n`Refuse`}
				</ActionButton>
			</menu>
		</>
	)
}

export default RefuseTaskController
